<template>
  <!-- Two roots on purpose: on phones the plate is pinned to the viewport while
       the crest scrolls away with the page. One wrapper could not hold both,
       since anything inside a fixed element is fixed with it. -->
  <router-link
    to="/"
    class="crest"
    :class="{ 'crest--home': isHome }"
    aria-label="ami*té — zur Startseite"
  >
    <img :src="crest" alt="" width="1000" height="644" />
  </router-link>

  <header class="nav">
    <nav
      ref="plate"
      class="nav__plate"
      :class="{ 'nav__plate--open': open }"
      aria-label="Hauptnavigation"
    >
      <button
        ref="toggle"
        type="button"
        class="nav__toggle"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-label="open ? 'Menü schließen' : 'Menü öffnen'"
        aria-controls="nav-menu"
        @click="open = !open"
      >
        <span class="nav__bars" aria-hidden="true">
          <span class="nav__bar nav__bar--top"></span>
          <span class="nav__bar nav__bar--mid"></span>
          <span class="nav__bar nav__bar--bottom"></span>
        </span>
      </button>

      <!-- The overlay stays inside the plate so onDocumentClick keeps working;
           that also puts it inside the plate for the outside-click test, so the
           panel's own empty ground has to close the menu itself. -->
      <div id="nav-menu" class="nav__menu">
        <div class="nav__menu-panel" @click.self="open = false">
          <!-- The mark, not a second way home: Shop already goes there, and two
               links to the same page in one menu is one too many. -->
          <img
            class="nav__menu-crest"
            :src="crest"
            alt=""
            aria-hidden="true"
            width="1000"
            height="644"
          >
          <p class="nav__menu-rule" aria-hidden="true">◆</p>

          <div class="nav__menu-inner" @click.self="open = false">
            <router-link to="/" class="nav__link">Shop</router-link>
            <router-link to="/about" class="nav__link">Über uns</router-link>
            <router-link to="/contact" class="nav__link">Kontakt</router-link>
            <!-- Carries .nav__link rather than a class of its own: the lozenge
                 is drawn by `.nav__link + .nav__link::before`, so a fourth
                 entry that wants the separator has to be a fourth .nav__link.
                 Being a button is what the block below has to undo. -->
            <button
              type="button"
              class="nav__link nav__link--button"
              aria-haspopup="dialog"
              @click="openCart"
            >Warenkorb<template v-if="cart.itemCount"> ({{ cart.itemCount }})</template></button>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import crest from '../assets/brand/crest.webp';
import { cartStore } from '@/store/cart.js';
import { lockScroll, unlockScroll, forgetScrollOffset } from '@/store/scrollLock.js';

const LOCK_HOLDER = 'nav-menu';

export default {
  name: 'SiteNav',
  data() {
    return { crest, open: false, cart: cartStore };
  },
  computed: {
    // Only the home page has a carpet for the crest to lie down on; everywhere
    // else it stays a corner mark.
    isHome() {
      return this.$route.name === 'Home';
    },
  },
  watch: {
    // Every close path runs through `open`, so the menu claims and releases the
    // shared lock in exactly one place. The drawer holds its own claim on the
    // same lock, and the body stays pinned while either of them does.
    open(isOpen) {
      if (isOpen) lockScroll(LOCK_HOLDER);
      else unlockScroll(LOCK_HOLDER);
    },
    // Tapping a link navigates; the panel must not stay open over the new page.
    $route() {
      forgetScrollOffset();
      this.open = false;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    // A lock outlives the component otherwise, and nothing else can release it.
    unlockScroll(LOCK_HOLDER);
  },
  methods: {
    /* On the phone the entry lives in the full-page menu, and the menu has to
       be gone before the drawer arrives — two overlays at once is not a state
       this header has. Focus goes to the trigger first so that the drawer,
       which hands focus back to whatever opened it, has something visible to
       hand it back to. */
    openCart() {
      if (this.open) {
        this.open = false;
        this.$refs.toggle.focus();
      }
      this.cart.openDrawer();
    },
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return;
      this.open = false;
      // Escape should hand the caret back to what opened the panel.
      this.$refs.toggle.focus();
    },
    onDocumentClick(event) {
      // Against the plate, not $el: a multi-root component has no single
      // element to test the click against.
      if (this.open && !this.$refs.plate.contains(event.target)) this.open = false;
    },
  },
};
</script>

<style scoped>
/* Absolute, not fixed — it sits on the floor and the floor moves. */
.crest {
  --crest-width: min(26vw, 175px);

  position: absolute;
  top: 1.75rem;
  left: clamp(1rem, 3.5vw, 3rem);
  z-index: 99;
  display: block;
  width: var(--crest-width);
  border-radius: 2px;
  transition: opacity 0.25s ease;
}

.crest img {
  display: block;
  width: 100%;
  height: auto;
  /* Cream first, so the thin black rules stay legible where the carpet runs
     dark; the brown pass underneath grounds it on the floor. */
  filter:
    drop-shadow(0 0 3px rgba(250, 246, 240, 0.95))
    drop-shadow(0 0 9px rgba(250, 246, 240, 0.75))
    drop-shadow(0 6px 10px rgba(40, 28, 18, 0.45));
}

.crest:hover {
  opacity: 0.82;
}

.crest:focus-visible {
  outline: 2px solid #2e4057;
  outline-offset: 8px;
}

/* Home only, and desktop only — scoped up here so the phone block below has
   nothing to undo. The crest lies on the carpet between the first two figures,
   as one more thing resting on the floor, and scrolls off with them. */
@media (min-width: 681px) {
  .crest--home {
    /* Wide enough to fill the gap the row leaves between the two figures,
       narrow enough not to reach into either one's rotation overhang. */
    --crest-width: min(35vw, 620px);

    /* The floor's own top padding plus a share of its figure height, both
       terms copied from Home.vue's .floor: SiteNav is mounted beside the view,
       not inside it, so it can only be told where the first row lands. The
       plate above contributes nothing — it is out of flow, so this offset is
       the only thing holding the crest down. */
    top: calc(max(13vh, var(--header-clearance)) + min(42vw, 620px) * 0.2);
    left: 0;
    right: 0;
    width: auto;
    display: flex;
    justify-content: center;
    /* The strip spans the page: click-through, so the two figures it reaches
       across keep their own links. The artwork takes the pointer back below. */
    pointer-events: none;
  }

  .crest--home img {
    width: var(--crest-width);
    pointer-events: auto;

    /* Laid onto the floor with the rows around it — the delay puts it between
       the first pair and the second, which is where it lies. The class is only
       on the home page, so arriving there is what starts this, and leaving
       takes it away again. */
    animation: crest-settle 0.65s cubic-bezier(0.22, 0.61, 0.36, 1) 0.18s both;
  }

  @keyframes crest-settle {
    from {
      opacity: 0;
      transform: translateY(22px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .crest--home img {
      animation: none;
    }
  }

  /* The ring belongs on the artwork, not on the full-width strip. */
  .crest--home:focus-visible {
    outline: none;
  }

  .crest--home:focus-visible img {
    outline: 2px solid #2e4057;
    outline-offset: 8px;
  }
}

/* Not fixed: the plate is ordinary page furniture now and scrolls off the top
   with everything else. Absolute so it still costs the views no flow height —
   they clear it with --header-clearance. */
.nav {
  position: absolute;
  top: 1.75rem;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: env(safe-area-inset-top) clamp(1rem, 3.5vw, 3rem) 0;
  pointer-events: none;
}

/* A plate resting on the carpet rather than a bar spanning the window: the
   drop shadow puts it above the weave, the translucency keeps it part of it. */
.nav__plate {
  --gap: 2.2rem;

  position: relative;
  flex: none;
  padding: 1.05rem 2.3rem;
  pointer-events: auto;

  background: rgba(250, 246, 240, 0.94);
  border: 1px solid rgba(58, 43, 30, 0.34);
  border-radius: 2px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.6),
    0 12px 26px -14px rgba(50, 35, 20, 0.65);
}

/* The second, inset line — Persian borders are drawn as paired rules. */
.nav__plate::before {
  content: '';
  position: absolute;
  inset: 4px;
  border: 1px solid rgba(58, 43, 30, 0.26);
  border-radius: 1px;
  pointer-events: none;
}

/* Desktop shows all three labels, so the trigger has nothing to do. */
.nav__toggle {
  display: none;
}

.nav__menu-inner {
  display: flex;
  gap: var(--gap);
}

/* Both belong to the phone's full-window panel; desktop shows the three labels
   in a plate that has no room for a mark above them. */
.nav__menu-crest,
.nav__menu-rule {
  display: none;
}

.nav__link {
  position: relative;
  color: #3a2b1e;
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  white-space: nowrap;
  /* Keeps the touch target at 44px without inflating the plate. */
  padding: 0.55rem 0;
  margin: -0.55rem 0;
  transition: color 0.25s ease;
}

/* Everything a button brings that a link does not; the label itself is styled
   by .nav__link above, which is what keeps the four entries one row. */
.nav__link--button {
  font-family: inherit;
  background: none;
  border: 0;
  cursor: pointer;
}

/* Lozenges between the links: the diamond is the motif the weave is built on. */
.nav__link + .nav__link::before {
  content: '◆';
  position: absolute;
  left: calc(var(--gap) / -2);
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.55rem;
  letter-spacing: 0;
  color: rgba(58, 43, 30, 0.5);
  pointer-events: none;
}

.nav__link:hover,
.nav__link:focus-visible {
  color: #2e4057;
}

.nav__link:focus-visible {
  outline: 2px solid #2e4057;
  outline-offset: 6px;
  border-radius: 1px;
}

/* Indigo, the dye that sits opposite madder red in a Persian palette. */
.nav__link.router-link-exact-active {
  color: #2e4057;
}

.nav__link.router-link-exact-active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0.3rem;
  height: 1px;
  background: #2e4057;
}

/* Below this the three labels stop resting clear of the crest, so the plate
   shrinks to a trigger in the corner and the labels move to a panel that takes
   the whole window. */
@media (max-width: 680px) {
  /* Shared here, and only here: the phone is the one layout where the plate
     still rides the crest's mid-line. 322/500 is the artwork's own ratio. */
  .crest,
  .nav {
    --crest-width: min(38vw, 150px);
  }

  .crest {
    top: 0.9rem;
    left: 50%;
    transform: translateX(-50%);
  }

  .nav {
    position: fixed;
    top: 0.9rem;
    justify-content: flex-end;
    height: calc(var(--crest-width) * 0.644);
  }

  .nav__plate {
    --gap: 2.4rem;

    padding: 0;
  }

  .nav__toggle {
    display: block;
    /* The plate already draws the border and the fill. */
    background: none;
    border: 0;
    padding: 0.95rem;
    cursor: pointer;
    color: inherit;
    /* Above the overlay, so the button that opened the menu is the same one
       that shuts it. Padding and box are untouched — this only restacks.
       It needs no ground of its own: shut, the plate is behind it; open, the
       panel is. A box drawn here would only cut into the panel's rule. */
    position: relative;
    z-index: 2;
  }

  .nav__toggle:focus-visible {
    outline: 2px solid #2e4057;
    outline-offset: -6px;
    border-radius: 1px;
  }

  .nav__bars {
    position: relative;
    display: block;
    width: 22px;
    height: 15px;
  }

  .nav__bar {
    position: absolute;
    left: 50%;
    width: 22px;
    height: 1.5px;
    background: #3a2b1e;
    transform: translateX(-50%);
    transition: transform 0.32s ease, opacity 0.2s ease;
  }

  .nav__bar--top {
    top: 0;
  }

  .nav__bar--bottom {
    top: 13.5px;
  }

  /* Not a third bar: the same lozenge that separates the labels, so the closed
     trigger reads as the woven border rather than as a generic hamburger. */
  .nav__bar--mid {
    top: 4.5px;
    width: 5.5px;
    height: 5.5px;
    transform: translateX(-50%) rotate(45deg);
  }

  .nav__plate--open .nav__bar--top {
    transform: translateX(-50%) translateY(6.75px) rotate(45deg);
  }

  .nav__plate--open .nav__bar--bottom {
    transform: translateX(-50%) translateY(-6.75px) rotate(-45deg);
  }

  .nav__plate--open .nav__bar--mid {
    opacity: 0;
    transform: translateX(-50%) rotate(45deg) scale(0);
  }

  /* The ground the panel lies on, and the whole window: fixed rather than
     absolute, so it answers to the viewport instead of to the trigger it hangs
     from. The plate is not a containing block for it — nothing above it is
     transformed — so inset: 0 really means the window. */
  .nav__menu {
    position: fixed;
    inset: 0;
    z-index: 1;
    display: flex;

    /* Hidden links must leave the tab order and not only the view — opacity or
       a transform alone would still let a tab land on them. */
    visibility: hidden;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease, visibility 0s linear 0.3s;
  }

  .nav__plate--open .nav__menu {
    visibility: visible;
    pointer-events: auto;
    opacity: 1;
    /* Only the delay, never the shorthand: restating `transition` here would
       outrank the reduced-motion block, which can only reach `.nav__menu`. */
    transition-delay: 0s;
  }

  /* Opaque, and edge to edge. Translucent it let the figures and the crest
     behind it through as smears; and once it is opaque there is nothing for a
     carpet margin around it to do, so the window edge is the panel edge and the
     paired rule alone draws the frame.
     The safe-area padding on body cannot reach a viewport-fixed box, so the
     panel keeps its own distance from the notch and the home bar. */
  .nav__menu-panel {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding:
      calc(env(safe-area-inset-top) + 2.4rem)
      calc(env(safe-area-inset-right) + 2.4rem)
      calc(env(safe-area-inset-bottom) + 2.4rem)
      calc(env(safe-area-inset-left) + 2.4rem);

    background: #faf6f0;
    transform: translateY(-10px);
    transition: transform 0.3s ease;
  }

  .nav__plate--open .nav__menu-panel {
    transform: translateY(0);
  }

  /* The paired rule the plates are drawn with, here as the panel's only frame.
     Held clear of the safe areas so the line does not run under the notch, and
     inset less than the trigger's own offset so the ✕ stays inside it. */
  .nav__menu-panel::before {
    content: '';
    position: absolute;
    inset:
      calc(env(safe-area-inset-top) + 0.6rem)
      calc(env(safe-area-inset-right) + 0.6rem)
      calc(env(safe-area-inset-bottom) + 0.6rem)
      calc(env(safe-area-inset-left) + 0.6rem);
    border: 1px solid rgba(58, 43, 30, 0.3);
    border-radius: 1px;
    pointer-events: none;
  }

  .nav__menu-crest {
    display: block;
    width: min(46vw, 210px);
    height: auto;
  }

  .nav__menu-rule {
    display: block;
    margin: 1.1rem 0 2.6rem;
    color: rgba(58, 43, 30, 0.5);
    font-size: 0.7rem;
  }

  .nav__menu-inner {
    flex-direction: column;
    align-items: center;
  }

  .nav__link {
    font-size: clamp(1.4rem, 7.5vw, 2.1rem);
    letter-spacing: 0.14em;
    padding: 0.35rem 0;
    margin: 0;
  }

  /* Stacked, the lozenge belongs above each label rather than beside it. */
  .nav__link + .nav__link::before {
    left: 50%;
    top: calc(var(--gap) / -2);
    font-size: 0.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .nav__link,
  .crest,
  .nav__bar,
  .nav__menu,
  .nav__menu-panel {
    transition: none;
  }
}
</style>
